import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, LogOut, Mail, RefreshCw } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { festivals, fmtDay } from "@/utils/festivals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const nextDays = (n) => {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i += 1) { const d = new Date(today); d.setDate(d.getDate() + i); out.push(fmtDay(d)); }
  return out;
};

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem("ttd_admin_token") || "");
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [proofs, setProofs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [digestStatus, setDigestStatus] = useState("");
  const [upiId, setUpiId] = useState("");
  const [upiInput, setUpiInput] = useState("");
  const [upiStatus, setUpiStatus] = useState("");

  const authHeaders = (tok) => ({ headers: { Authorization: `Bearer ${tok || token}` } });

  const load = async (tok) => {
    setLoading(true);
    try {
      const [occ, proofList, upi] = await Promise.all([
        axios.get(`${API}/admin/occupancy`, authHeaders(tok)),
        axios.get(`${API}/admin/payment-proofs`, authHeaders(tok)),
        axios.get(`${API}/settings/upi`),
      ]);
      setData(occ.data);
      setProofs(proofList.data.proofs);
      setUpiId(upi.data.upi_id);
      setUpiInput(upi.data.upi_id);
      setError("");
    } catch {
      localStorage.removeItem("ttd_admin_token");
      setToken("");
      setData(null);
      setProofs(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("ttd_admin_token", r.data.access_token);
      setToken(r.data.access_token);
      load(r.data.access_token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    }
  };

  const sendDigest = async () => {
    setDigestStatus("Sending…");
    try {
      await axios.post(`${API}/admin/send-digest`, {}, authHeaders());
      setDigestStatus("Digest emailed to the admin inbox.");
    } catch {
      setDigestStatus("Digest could not be sent right now — please try again later.");
    }
  };

  const setPaymentVerified = async (bookingId, verified) => {
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/payment`, { verified }, authHeaders());
      setProofs((prev) => prev.map((p) => (p.booking_id === bookingId ? { ...p, payment_verified: verified } : p)));
    } catch { /* keep list as-is on failure */ }
  };

  const saveUpi = async (e) => {
    e.preventDefault();
    setUpiStatus("Saving…");
    try {
      const r = await axios.post(`${API}/admin/settings/upi`, { upi_id: upiInput }, authHeaders());
      setUpiId(r.data.upi_id);
      setUpiInput(r.data.upi_id);
      setUpiStatus("UPI ID updated — the payment page shows it instantly.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUpiStatus(typeof detail === "string" ? detail : "Could not update the UPI ID.");
    }
  };

  const logout = () => { localStorage.removeItem("ttd_admin_token"); setToken(""); setData(null); setProofs(null); };
  const days = nextDays(14);
  const pendingCount = proofs ? proofs.filter((p) => !p.payment_verified && p.status !== "cancelled").length : 0;

  return <div className="site-shell">
    <header className="topbar"><div className="topbar-inner">
      <Link className="brand" to="/" data-testid="admin-brand-link"><img src="https://tt-devasthanam-booking-349466.hostingersite.com/ttd-logo.png" alt="TT Devasthanam" /><span><b>TT Devasthanam</b><small>Admin — Occupancy</small></span></Link>
      {token
        ? <button className="secure-chip verify-back admin-action" onClick={logout} data-testid="admin-logout-button"><LogOut size={15} /> Logout</button>
        : <Link className="secure-chip verify-back" to="/" data-testid="admin-back-home-link"><ArrowLeft size={15} /> Back to site</Link>}
    </div></header>
    <main className="verify-page admin-page">
      {!token ? (
        <div className="verify-card admin-login" data-testid="admin-login-card">
          <p className="eyebrow"><span className="eyebrow-line" /> Staff only</p>
          <h3>Admin sign in</h3>
          <p>Occupancy across all sevas, day by day and slot by slot.</p>
          <form onSubmit={login}>
            <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin email" data-testid="admin-email-input" /></label>
            <label>Password<input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="password" data-testid="admin-password-input" /></label>
            {error && <p className="form-error" data-testid="admin-login-error">{error}</p>}
            <button className="primary-button form-submit" type="submit" data-testid="admin-login-button">Sign in</button>
          </form>
        </div>
      ) : (
        <>
          <div className="admin-head">
            <div><p className="eyebrow"><span className="eyebrow-line" /> Live occupancy</p><h1 className="verify-title">Seva <em>occupancy.</em></h1></div>
            <div className="admin-actions">
              <button className="secondary-button" onClick={() => load()} disabled={loading} data-testid="admin-refresh-button"><RefreshCw size={15} /> {loading ? "Refreshing…" : "Refresh"}</button>
              <button className="secondary-button" onClick={sendDigest} data-testid="admin-digest-button"><Mail size={15} /> Email digest now</button>
            </div>
          </div>
          <p className="digest-hint">A digest like this is also emailed automatically every morning at 7:00 AM IST.</p>
          {digestStatus && <p className="email-status ok" data-testid="admin-digest-status"><Mail size={14} /> {digestStatus}</p>}

          <div className="verify-card" data-testid="upi-settings-card">
            <h3>UPI payment ID</h3>
            <p>Pilgrims pay to this UPI ID on the booking payment page. Changes apply instantly to new payment screens.</p>
            <div className="upi-current">Current: <b data-testid="upi-current-text">{upiId || "…"}</b></div>
            <form className="upi-form" onSubmit={saveUpi}>
              <label>New UPI ID<input value={upiInput} onChange={(e) => setUpiInput(e.target.value)} placeholder="name@bank" data-testid="upi-input" /></label>
              <button className="primary-button" type="submit" data-testid="upi-save-button">Update UPI ID</button>
            </form>
            {upiStatus && <p className="email-status ok resend-status" data-testid="upi-status">{upiStatus}</p>}
          </div>

          <div className="verify-card admin-proofs" data-testid="payment-proofs-section">
            <h3>Payment proofs</h3>
            <p>{proofs === null ? "Loading…" : proofs.length === 0 ? "No payment screenshots yet." : `${pendingCount} awaiting review · ${proofs.length} total`}</p>
            {proofs && proofs.map((p) => (
              <div className="proof-row" key={p.booking_id} data-testid={`proof-${p.booking_id}`}>
                <a href={`${API}/files/${p.payment_proof}`} target="_blank" rel="noreferrer">
                  <img src={`${API}/files/${p.payment_proof}`} alt={`Payment proof for ${p.booking_id}`} className="proof-thumb" data-testid={`proof-img-${p.booking_id}`} />
                </a>
                <div className="proof-meta">
                  <b>{p.name} · {p.booking_id}</b>
                  <span>{p.service} — {p.date}{p.slot ? ` · ${p.slot} hrs` : ""} · ₹{Number(p.amount).toLocaleString("en-IN")}</span>
                  <span>{p.pilgrim_details?.length || p.pilgrims} pilgrim(s) · status: {p.status || "confirmed"}{p.entered_at ? " · entered" : ""}</span>
                </div>
                {p.payment_verified ? (
                  <span className="proof-badge" data-testid={`proof-verified-${p.booking_id}`}>
                    <BadgeCheck size={14} /> Verified
                    <button className="proof-undo" onClick={() => setPaymentVerified(p.booking_id, false)} data-testid={`proof-undo-${p.booking_id}`}>undo</button>
                  </span>
                ) : (
                  <button className="copy-btn" onClick={() => setPaymentVerified(p.booking_id, true)} data-testid={`proof-verify-btn-${p.booking_id}`}><BadgeCheck size={14} /> Mark verified</button>
                )}
              </div>
            ))}
          </div>

          {!data ? <p className="section-description">Loading occupancy…</p> : data.services.map((s) => (
            <div className="verify-card admin-service" key={s.name} data-testid={`occ-${s.name.slice(0, 12).replace(/\W/g, "-")}`}>
              <h3>{s.name}</h3>
              <p>{s.slots.length ? `${s.slots.length} slots/day · ${data.slot_capacity} seats per slot` : `${data.daily_capacity} seats per day`}</p>
              <div className="occ-scroll"><table className="occ-table">
                <thead><tr><th>Date</th>{s.slots.length ? s.slots.map((sl) => <th key={sl}>{sl}</th>) : <th>Booked</th>}<th>Left</th></tr></thead>
                <tbody>{days.map((d) => {
                  const booked = s.dates[d] || {};
                  if (s.slots.length) {
                    const totalBooked = s.slots.reduce((sum, sl) => sum + (booked[sl] || 0), 0);
                    const remaining = s.slots.length * data.slot_capacity - totalBooked;
                    return <tr key={d} className={remaining <= 10 ? "low" : ""} data-testid={`occ-row-${d}`}>
                      <td>{d.slice(8, 10)}-{d.slice(5, 7)}{festivals[d] ? " ✦" : ""}</td>
                      {s.slots.map((sl) => <td key={sl}>{booked[sl] || 0}/{data.slot_capacity}</td>)}
                      <td><b>{remaining}</b></td>
                    </tr>;
                  }
                  const b = booked[""] || 0;
                  return <tr key={d} className={data.daily_capacity - b <= 20 ? "low" : ""} data-testid={`occ-row-${d}`}>
                    <td>{d.slice(8, 10)}-{d.slice(5, 7)}{festivals[d] ? " ✦" : ""}</td>
                    <td>{b}/{data.daily_capacity}</td>
                    <td><b>{data.daily_capacity - b}</b></td>
                  </tr>;
                })}</tbody>
              </table></div>
            </div>
          ))}
        </>
      )}
    </main>
    <WhatsAppButton />
  </div>;
}
